import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useQuotationDraftStore } from '../store/quotationDraftStore';
import { IntakeSourceForm } from './IntakeSourceForm';
import type { QuotationDraft } from '../types/quotation.types';

interface ExtractFromSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: QuotationDraft;
}

export function ExtractFromSourceDialog({ open, onOpenChange, draft }: ExtractFromSourceDialogProps) {
  const extractIntoDraft = useQuotationDraftStore((s) => s.extractIntoDraft);
  const itemCount = draft.lineItems.length;
  const hasItems = itemCount > 0;

  const submitLabel = hasItems
    ? `Replace ${itemCount} item${itemCount === 1 ? '' : 's'} & extract`
    : 'Extract items';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Extract line items from source</DialogTitle>
          <DialogDescription>
            Paste text or upload a file — the AI will extract rebar line items into this draft.
          </DialogDescription>
        </DialogHeader>

        {hasItems && (
          <div className="flex gap-2 rounded-md border border-warning/30 bg-warning/10 p-3 text-xs text-warning">
            <AlertTriangle className="h-4 w-4 shrink-0 text-warning mt-0.5" />
            <div>
              <p className="font-semibold">Heads up</p>
              <p className="mt-0.5">
                This will replace the {itemCount} line item{itemCount === 1 ? '' : 's'} currently on the
                draft. Client / Project fields you&apos;ve typed will be kept.
              </p>
            </div>
          </div>
        )}

        <IntakeSourceForm
          submitLabel={submitLabel}
          onSubmit={(source) => {
            extractIntoDraft(draft.id, source);
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
